import { addDays } from "@visioni/a3s-dates";

import { StartIncidentsRecomputeRequest } from "../../client-messages/incidents-recompute";
import { wallClockStart } from "./wall-clock";

/**
 * The recompute for whole days chosen in `timeZone`: from midnight on the
 * first day to midnight after the last, as the UTC instants the batch counts
 * in. A day the clocks change on is covered whole, however long it is.
 *
 * `error` explains why those days can't be recomputed as chosen; neither is set
 * until both days are.
 */
export function incidentsRecomputePeriod(
  days: { readonly from?: string | undefined; readonly to?: string | undefined },
  timeZone: string,
  now: Date,
): { readonly request?: StartIncidentsRecomputeRequest; readonly error?: string } {
  if (days.from === undefined || days.to === undefined) return {};
  const since = wallClockStart(days.from, timeZone);
  const until = wallClockStart(addDays(days.to, 1), timeZone);
  if (since === undefined || until === undefined) return {};

  if (Date.parse(until) > now.getTime()) {
    return { error: `${days.to} hasn't ended yet in ${timeZone}. Recompute it once it has.` };
  }
  const parsed = StartIncidentsRecomputeRequest.safeParse({ since, until });
  if (!parsed.success) {
    const message = parsed.error.issues[0]?.message ?? "These days can't be recomputed";
    return {
      error:
        message.includes("half-hour") ?
          `Midnight in ${timeZone} isn't on a half-hour in UTC, where incidents are counted, so its days can't be recomputed whole.`
        : message,
    };
  }
  return { request: parsed.data };
}
